import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { axiosErrorHandler } from 'common/helpers/axios.helper';
import { DEFAULT_VALUE_0 } from 'common/config';
import { AppDispatch, RootState } from 'app/store';
import {
  IRequestHistory,
  ICreateRequestHistoryCommentPayload,
  IRequestHistoryListParams,
  IRequestHistoryStats,
  IRequestHistoryStatsPayload,
} from 'entities/RequestHistory/RequestHistory.models';
import { requestHistoryTransport } from 'entities/RequestHistory/RequestHistory.transport';

interface IState {
  requestHistoryList: IRequestHistory[];
  requestHistoryCount: number;
  isRequestHistoryLoading: boolean;
  priceChangeHistory: IRequestHistory[];
  requestHistoryStats: IRequestHistoryStats | null;
}

const initialState: IState = {
  requestHistoryList: [],
  requestHistoryCount: 0,
  isRequestHistoryLoading: false,
  priceChangeHistory: [],
  requestHistoryStats: {
    count: DEFAULT_VALUE_0,
    unread: DEFAULT_VALUE_0,
  },
};

export const requestHistorySlice = createSlice({
  name: 'requestHistory',
  initialState,
  reducers: {
    setRequestHistoryList(state, action: PayloadAction<IRequestHistory[]>) {
      state.requestHistoryList = action.payload;
    },
    setRequestHistoryCount(state, action: PayloadAction<number>) {
      state.requestHistoryCount = action.payload;
    },
    setIsRequestHistoryLoading(state, action: PayloadAction<boolean>) {
      state.isRequestHistoryLoading = action.payload;
    },
    setPriceChangeHistory(state, action: PayloadAction<IRequestHistory[]>) {
      state.priceChangeHistory = action.payload;
    },
    setRequestHistoryStats(state, action: PayloadAction<IRequestHistoryStats | null>) {
      state.requestHistoryStats = action.payload;
    },
    addRequestHistoryListItem(state, action: PayloadAction<IRequestHistory>) {
      state.requestHistoryList.push(action.payload);
    },
  },
});

export const {
  setRequestHistoryList,
  setRequestHistoryCount,
  setIsRequestHistoryLoading,
  setPriceChangeHistory,
  setRequestHistoryStats,
  addRequestHistoryListItem,
} = requestHistorySlice.actions;

export const selectRequestHistoryList = (state: RootState) => state.requestHistory.requestHistoryList;
export const selectRequestHistoryCount = (state: RootState) => state.requestHistory.requestHistoryCount;
export const selectIsRequestHistoryLoading = (state: RootState) => state.requestHistory.isRequestHistoryLoading;
export const selectPriceChangeHistory = (state: RootState) => state.requestHistory.priceChangeHistory;
export const selectRequestHistoryStats = (state: RootState) => state.requestHistory.requestHistoryStats;

export const requestHistoryReducer = requestHistorySlice.reducer;

export const getRequestHistoryList = (params: IRequestHistoryListParams) => async (dispatch: AppDispatch) => {
  dispatch(setIsRequestHistoryLoading(true));

  try {
    const response = await requestHistoryTransport.get(params);
    dispatch(setRequestHistoryList(response.data));
    dispatch(setRequestHistoryCount(response.count));
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsRequestHistoryLoading(false));
  }
};

export const createRequestHistoryComment = (payload: ICreateRequestHistoryCommentPayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsRequestHistoryLoading(true));

  try {
    const response = await requestHistoryTransport.createComment(payload);
    dispatch(addRequestHistoryListItem(response));
    payload.onSuccess?.();
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsRequestHistoryLoading(false));
  }
};

export const getPriceChangeHistory = (params: IRequestHistoryListParams) => async (dispatch: AppDispatch) => {
  dispatch(setIsRequestHistoryLoading(true));

  try {
    const response = await requestHistoryTransport.get(params);
    dispatch(setPriceChangeHistory(response.data));
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsRequestHistoryLoading(false));
  }
};

export const getRequestHistoryStats = (payload: IRequestHistoryStatsPayload) => async (dispatch: AppDispatch) => {
  try {
    const response = await requestHistoryTransport.getStats(payload);
    dispatch(setRequestHistoryStats(response));
  } catch (error) {
    axiosErrorHandler(error);
  }
};
